<template>
  <div
    class="clip-editor-container"
    :style="`opacity: ${shouldShowEditor ? 1 : 0}`">
    <DateTimeModal v-if="isDateTimeModalShowing" />
    <ExportClipModal
      v-if="state.showExportClipModal"
      v-model="clipTitle"
      :onClose="onShowExportModal"
      :onExport="onSubmitExportModal" />
    <GlobalEvents
      v-if="shouldShowEditor"
      @keyup.space.prevent="onPressSpacePlay"
      @keydown.right.prevent="onSeekAdd"
      @keydown.left.prevent="onSeekSubtract"
      @keydown.ctrl.left.prevent="onSeekSubtractFrame"
      @keydown.ctrl.right.prevent="onSeekAddFrame"
      @keydown.alt.left.prevent="onSeekSubtractFrame"
      @keydown.alt.right.prevent="onSeekAddFrame" />
    <div class="editor-header">
      <div class="editor-header-title">
        <div class="timeline-header">
          <h4 class="timeline-text">Timeline</h4>
          <div class="button-container">
            <button
              type="button"
              class="btn source-button change-time-button"
              @click.stop="showDateTimeModal">
              {{$t('capture.timeline.selectDateTime')}}
            </button>
            <button
              v-if="!isLive"
              type="button"
              class="btn source-button change-time-button"
              @click.stop="goLive">
              {{$t('capture.timeline.goLive')}}
            </button>
          </div>
        </div>
        <h5 class="time-text">
          {{ currentDateForDisplaying }}
        </h5>
      </div>
      <div
        v-show="hasVideoInPeriod"
        class="timeline-timestamp-container">
        <h6 class="timeline-timestamp">
          {{ getTimeFromMs(playheadProgramDateTime) }}
        </h6>
      </div>
      <div
        v-show="hasVideoInPeriod"
        ref="sliderZoomContainer"
        class="timeline-zoom-container">
        <div class="zoom-text-hint">
          <p class="zoom-size">{{$t('capture.timeline.zoomRange')}}: {{getZoomSize()}}</p>
        </div>
        <div class="zoom-slider">
          <div class="slider-icon">
            <i
              @click.stop="zoomOut()"
              class="fas fa-search-minus zoom-out-icon">
            </i>
          </div>
          <div
            ref="sliderTrack"
            class="slider-track">
            <div
              ref="sliderButton"
              class="current-point" />
            <div
              class="slider-point"
              @click.stop="setInterval(3600)" />
            <div
              class="slider-point"
              @click.stop="setInterval(1800)" />
            <div
              class="slider-point"
              @click.stop="setInterval(900)" />
            <div
              class="slider-point"
              @click.stop="setInterval(300)" />
          </div>
          <div class="slider-icon">
            <i
              @click.stop="zoomIn()"
              class="fas fa-search-plus zoom-in-icon">
            </i>
          </div>
        </div>
      </div>
    </div>
    <div
      v-show="hasVideoInPeriod"
      class="clip-editor-wrapper">
      <div class="clip-editor"
           ref="container">
        <div
          ref="helpOverlay"
          class="editor-help-overlay"
          @click.stop="onClickTrimmer">
          <p class="help-text">
            {{$t('capture.timeline.helpText.base')}}
            <span class="yellow">
              {{$t('capture.timeline.helpText.color')}}
            </span>
          </p>
        </div>
        <div
          class="clip-editor__panner-container"
          ref="panner"
          :style="{width: calculatePannerWidthPercent}"
          @click.stop="onClickTrimmer">
          <template v-for="(duration) in calculatedChunks">
            <div
              class="time-chunk-container"
              :key="duration">
              <div class="time-chunk" />
              <div class="time-chunk__date">
                {{ duration }}
              </div>
            </div>
          </template>
        </div>
        <div
          ref="marker"
          class="clip-editor__marker"
          :style="{'transform': `translateX(${markerPos}px)`}">
          <div class="clip-editor__marker-time"></div>
        </div>
        <div
          class="clip-editor__dead-sequence"
          v-for="(fragment, index) in deadFragments"
          :key="index + 'deadSeq'"
          :style="{
            left: `${getDeadSequenceStart(fragment)}px`,
            width: `${getDeadSequenceWidth(fragment)}px`,
          }"
          @click.stop="onClickTrimmer" />
        <div
          class="clip-editor__sequence"
          :style="{
            'transform': `translateX(${sequenceStart}px)`,
            'width': `${sequenceWidth}px`,
          }"
          ref="sequence"
          @click.stop="onClickTrimmer" />
        <div
          class="clip-editor__trimmer-container"
          ref="trimmer"
          :style="{width: calculatePannerWidthPercent}"
          @click.stop="onClickTrimmer" />
      </div>
    </div>
    <TimelineMinimap v-show="hasVideoInPeriod"/>
    <div
      v-show="hasVideoInPeriod"
      class="timeline-toolbar">
      <button
        type="button"
        v-show="markOutProgramDateTime !== markInProgramDateTime"
        class="btn goto-start-button"
        @click.stop="onScrollToStart">
        <i class="fa fa-step-backward" />
        <span class="help-text">{{$t('capture.timeline.toolbar.button.seekToStart')}} <span class="key">Z</span></span>
      </button>
      <button
        type="button"
        class="btn set-start-button current-step"
        ref="setStartButton"
        @click.stop="setStart">
        【
        <span class="help-text">
          {{$t('capture.timeline.toolbar.button.base')}}<strong>{{$t('capture.timeline.toolbar.button.color')}}</strong>{{$t('capture.timeline.toolbar.button.setStart')}}
          <span class="key">X</span>
        </span>
      </button>
      <button
        type="button"
        class="btn play-button"
        v-if="!isPlaying"
        @click.stop="onPressSpacePlay">
        <i class="fas fa-play"></i>
        <span class="help-text">{{$t('capture.timeline.toolbar.button.play')}} <span class="key">Spacebar</span></span>
      </button>
      <button
        type="button"
        class="btn pause-button"
        v-if="isPlaying"
        @click.stop="onPressSpacePlay">
        <i class="fas fa-pause"></i>
        <span class="help-text">{{$t('capture.timeline.toolbar.button.pause')}} <span class="key">Spacebar</span></span>
      </button>
      <button
        type="button"
        class="btn set-end-button"
        ref="setEndButton"
        @click.stop="setEnd">
        】
        <span class="help-text">
          {{$t('capture.timeline.toolbar.button.base')}}<strong>{{$t('capture.timeline.toolbar.button.color')}}</strong>{{$t('capture.timeline.toolbar.button.setEnd')}}
          <span class="key">C</span>
        </span>
      </button>
      <button
        type="button"
        v-show="markOutProgramDateTime !== markInProgramDateTime"
        class="btn goto-end-button"
        @click="onScrollToEnd">
        <i class="fa fa-step-forward" />
        <span class="help-text">{{$t('capture.timeline.toolbar.button.seekToEnd')}} <span class="key">V</span></span>
      </button>
    </div>
    <div
      v-show="hasVideoInPeriod"
      class="timeline-actions-container">
      <div
        v-if="markInTimelineTime !== markOutTimelineTime"
        class="output-info">
        <p class="output-text"><strong>{{$t('capture.timeline.output.startTime')}}:</strong> {{ getTimeFromMs(markInProgramDateTime) }}</p>
        <p class="output-text"><strong>{{$t('capture.timeline.output.endTime')}}:</strong> {{ getTimeFromMs(markOutProgramDateTime) }}</p>
      </div>
      <button
        type="button"
        class="btn preview-clip-button"
        v-if="isValidToDownload"
        @click.stop="onPreviewClip">
        <i class="fas fa-eye"></i> {{$t('capture.timeline.actions.preview')}}
      </button>
      <button
        type="button"
        class="btn screenshot-button"
        @click.stop="onExportThumbnail">
        <span v-if="!statusMessage.isSaving"><i class="fas fa-camera"></i> {{$t('capture.timeline.actions.screenshot')}}</span>
      </button>
      <button
        type="button"
        class="btn btn-primary trim-button"
        @click.stop="onShowExportModal()"
        :disabled="!isValidToDownload">
        <span v-if="!statusMessage.isSaving"><i class="fas fa-cut"></i> {{$t('capture.timeline.actions.trim')}}</span>
        <span v-if="statusMessage.isSaving">{{$t('capture.timeline.actions.isTrimming')}}</span>
      </button>
    </div>
    <p
      v-if="!hasVideoInPeriod"
      class="error-message">
      {{$t('validation:dateTimeOutOfRange')}}
    </p>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import {
  addMilliseconds,
  differenceInSeconds,
  format,
  parseISO,
} from 'date-fns';
import scrollIntoView from 'scroll-into-view-if-needed';
import GlobalEvents from 'vue-global-events';

import ExportClipModal from './parts/clip/ExportClipModal.vue';
import TimelineMinimap from './parts/clip/TimelineMinimap.vue';
import DateTimeModal from './parts/clip/DateTimeModal.vue';

export default {
  props: [
    'video',
    'onSubmit',
    'statusMessage',
    'onExportThumbnail',
    'hasVideoInPeriod',
  ],
  components: {
    DateTimeModal,
    GlobalEvents,
    ExportClipModal,
    TimelineMinimap,
  },
  data() {
    return {
      state: {
        previewClip: false,
        latestPlay: 'play',
        showExportClipModal: false,
        shouldAutoScroll: true,
      },
      source: {
        start: '',
        end: '',
      },
      chunk: {
        start: 0,
        end: 0,
      },
      scale: 150,
      interval: 900,
      markerPos: 0,
      sequenceStart: 0,
      sequenceWidth: 0,
      sequenceEnd: 0,
      clipTitle: '',
      isDragging: false,
      mouseDownPosition: 0,
      mouseUpPosition: 0,
      isZoomDragging: false,
      isFirstButtonHovered: false,
      allButtonsHovered: false,
    };
  },
  computed: {
    ...mapState({
      duration: state => state.stream.timelineDuration / 1000,
      playheadTimelineTime: state => state.stream.playheadTimelineTime,
      deadFragments: state => state.stream.blackholeTimelines,
      markInTimelineTime: state => state.stream.markInTimelineTime,
      markOutTimelineTime: state => state.stream.markOutTimelineTime,
      isDateTimeModalShowing: state => state.timeline.isDateTimeModalShowing,
    }),
    ...mapGetters([
      'playheadProgramDateTime',
      'markInProgramDateTime',
      'markOutProgramDateTime',
      'selectedDateTime',
      'isLive',
      'isPlaying',
    ]),
    currentDateForDisplaying() {
      if (this.selectedDateTime.length > 0) {
        return format(parseISO(this.selectedDateTime), 'dd/MM/yyyy HH:mm:ss');
      }
      return this.$t('capture.timeline.isLive');
    },
    markerTime() {
      return this.playheadTimelineTime / 1000;
    },
    shouldShowEditor() {
      return !_.isEmpty(this.video.start) && !_.isEmpty(this.video.end);
    },
    rawSourceDuration() {
      if (this.duration && Number.isFinite(this.duration)) {
        return this.duration;
      }
      if (!_.isEmpty(this.source.end) && !_.isEmpty(this.source.start)) {
        const start = parseISO(this.source.start);
        const end = parseISO(this.source.start);
        return differenceInSeconds(end, start);
      }
      return 0;
    },
    sourceDuration() {
      return Math.floor(this.rawSourceDuration);
    },
    calculatedChunks() {
      const chunks = [];
      _.times(this.sourceDuration, (iterator) => {
        if (iterator % this.scale === 0) {
          chunks.push(this.getShortTime(iterator));
        }
      });
      return chunks;
    },
    calculatePannerWidthPercent() {
      return `${(this.sourceDuration / this.interval) * 100}%`;
    },
    isValidToDownload() {
      return this.markInTimelineTime !== this.markOutTimelineTime
        && !this.statusMessage.isSaving
        && this.video.src
        && this.chunk;
    },
    chunkPayload() {
      return {
        title: this.clipTitle,
        start: new Date(this.markInProgramDateTime),
        end: new Date(this.markOutProgramDateTime),
      };
    },
  },
  watch: {
    playheadTimelineTime(value) {
      if (this.$refs && this.$refs.container) {
        const containerWidth = this.$refs.container.clientWidth;
        this.markerPos = value / 1000 * containerWidth / this.interval;
      }
      // this.scrollToMarker();
    },
    video() {
      this.source = this.video;
    },
    interval() {
      switch (this.interval) {
        case 3600:
          this.scale = 600;
          break;
        case 1800:
          this.scale = 300;
          break;
        case 900:
          this.scale = 150;
          break;
        case 300:
          this.scale = 45;
          break;
        default:
          this.scale = 0;
      }
      this.adjustMarkerTime();
      this.adjustSequence();
    },
  },
  created() {
    this.source = this.video;
  },
  mounted() {
    if (!this.$refs.trimmer) {
      return;
    }

    window.addEventListener('keypress', this.keyListener);

    this.$refs.trimmer.addEventListener('mousedown', this.onMouseDown);
    this.$refs.trimmer.addEventListener('mousemove', this.onMouseMove);
    this.$refs.trimmer.addEventListener('mouseup', this.onMouseUp);
    this.$refs.marker.addEventListener('mousedown', this.onMouseDown);
    this.$refs.marker.addEventListener('mousemove', this.onMouseMove);
    this.$refs.marker.addEventListener('mouseup', this.onMouseUp);
    this.$refs.helpOverlay.addEventListener('mousedown', this.onMouseDown);
    this.$refs.helpOverlay.addEventListener('mousemove', this.onMouseMove);
    this.$refs.helpOverlay.addEventListener('mouseup', this.onMouseUp);
    this.$refs.sequence.addEventListener('mousedown', this.onMouseDown);
    this.$refs.sequence.addEventListener('mousemove', this.onMouseMove);
    this.$refs.sequence.addEventListener('mouseup', this.onMouseUp);

    this.$refs.sliderZoomContainer.addEventListener('mousedown', this.onSliderButtonMouseDown);
    this.$refs.sliderZoomContainer.addEventListener('mousemove', this.onSliderButtonMouseMove);
    this.$refs.sliderZoomContainer.addEventListener('mouseup', this.onSliderButtonMouseUp);

    this.$refs.setStartButton.addEventListener('mouseenter', this.onHoverStartButton);
    this.$refs.setEndButton.addEventListener('mouseenter', this.onHoverEndButton);
  },
  methods: {
    ...mapActions([
      'showDateTimeModal',
      'goLive',
    ]),
    keyListener(event) {
      const key = String.fromCharCode(event.keyCode).toLowerCase();
      switch (key) {
        case 'x':
          this.setStart();
          break;
        case 'c':
          this.setEnd();
          break;
        case 'z':
          this.onScrollToStart();
          break;
        case 'v':
          this.onScrollToEnd();
          break;
        default:
          break;
      }
    },
    getTimeFromMs(ms) {
      return format(new Date(ms), 'HH:mm:ss');
    },
    getShortTime(seconds) {
      if (this.source.start) {
        // NOTE: Use addMilliseconds to accounted for decimals of seconds
        if (seconds === 1) return format(parseISO(this.source.start), 'HH:mm:ss');
        return format(addMilliseconds(parseISO(this.source.start), seconds * 1000), 'HH:mm:ss');
      }
      // Returning 0 causes duplicated key error
      return Math.random();
    },
    setMaxMouseUpPosition(position) {
      if (this.maxMouseUpPosition < position) {
        this.maxMouseUpPosition = position;
      }
    },
    onHoverStartButton() {
      this.isFirstButtonHovered = true;
      this.$refs.setStartButton.style.animation = 'unset';
      if (!this.allButtonsHovered) {
        this.$refs.setEndButton.style.animation = 'background-blinking 1500ms infinite';
      }
    },
    onHoverEndButton() {
      if (this.isFirstButtonHovered) {
        this.$refs.setEndButton.style.animation = 'unset';
        this.allButtonsHovered = true;
      }
    },
    // eslint-disable-next-line no-unused-vars
    onSliderButtonMouseDown(event) {
      // this.mouseDownPosition = event.clientX - this.$refs.sliderTrack.getBoundingClientRect().x;
      this.isZoomDragging = true;
    },
    // eslint-disable-next-line no-unused-vars
    onSliderButtonMouseUp(event) {
      this.isZoomDragging = false;
      this.$refs.sliderZoomContainer.style.cursor = 'default';
    },
    onSliderButtonMouseMove(event) {
      if (this.isZoomDragging) {
        const sliderTrackPosition = event.clientX - this.$refs.sliderTrack.getBoundingClientRect().x;
        const containerPosition = event.clientX - this.$refs.sliderZoomContainer.getBoundingClientRect().x;
        this.$refs.sliderZoomContainer.style.cursor = 'ew-resize';
        if (sliderTrackPosition === 0) {
          this.interval = 3600;
        }
        if (sliderTrackPosition > 0 && sliderTrackPosition < 67) {
          this.interval = 1800;
        }
        if (sliderTrackPosition >= 67 && sliderTrackPosition < 135) {
          this.interval = 900;
        }
        if (sliderTrackPosition >= 135 && sliderTrackPosition < 200) {
          this.interval = 300;
        }
        this.moveSliderButton();
        if (containerPosition > 298 || containerPosition < 2) {
          this.isZoomDragging = false;
          this.$refs.sliderZoomContainer.style.cursor = 'default';
        }
      }
    },
    moveSliderButton() {
      const button = this.$refs.sliderButton;
      switch (this.interval) {
        case 3600:
          button.style.left = '-4px';
          break;
        case 1800:
          button.style.left = '62px';
          break;
        case 900:
          button.style.left = '128px';
          break;
        default:
          button.style.left = '192px';
      }
    },
    onMouseDown(event) {
      this.mouseDownPosition = event.clientX - this.$refs.trimmer.getBoundingClientRect().x;
      this.isDragging = true;
    },
    // eslint-disable-next-line no-unused-vars
    onMouseUp(event) {
      this.isDragging = false;
      this.$refs.trimmer.style.cursor = 'pointer';
      this.markerPos = this.mouseUpPosition;
    },
    onMouseMove(event) {
      if (this.isDragging) {
        this.$refs.helpOverlay.style.display = 'none';
        this.mouseUpPosition = event.clientX - this.$refs.trimmer.getBoundingClientRect().x;
        if (this.mouseUpPosition - this.mouseDownPosition < 6) {
          return;
        }
        this.$refs.trimmer.style.cursor = 'ew-resize';
        const startTime = this.getTimeByPosition(this.mouseDownPosition);
        const endTime = this.getTimeByPosition(this.mouseUpPosition);
        this.$store.commit('setMarkInTimelineTime', startTime * 1000);
        this.$store.commit('setMarkOutTimelineTime', endTime * 1000);
        this.sequenceStart = this.mouseDownPosition;
        this.sequenceEnd = this.mouseUpPosition;
        this.sequenceWidth = this.sequenceEnd - this.sequenceStart;
      }
    },
    onClickTrimmer(event) {
      const pos = event.clientX - this.$refs.trimmer.getBoundingClientRect().x;
      const markerTime = this.getTimeByPosition(pos);
      this.$store.commit('setPlayheadByTimelineTime', markerTime * 1000);
    },
    adjustMarkerTime() {
      this.markerPos = this.getPositionByTime(this.markerTime);
      this.scrollToMarker();
    },
    adjustSequence() {
      this.sequenceStart = this.getPositionByTime(this.markInTimelineTime / 1000);
      this.sequenceEnd = this.getPositionByTime(this.markOutTimelineTime / 1000);
      let sequenceWidth = this.sequenceEnd - this.sequenceStart;
      if (sequenceWidth === 0) {
        sequenceWidth = 4;
      }
      this.sequenceWidth = sequenceWidth;
    },
    getTimeByPosition(pos, floor = true) {
      const containerWidth = this.$refs.container.clientWidth;
      const time = (pos * this.interval) / containerWidth;
      if (floor) {
        return Math.floor(time);
      }
      return time;
    },
    getPositionByTime(time) {
      const containerWidth = this.$refs.container.clientWidth;
      return Math.floor((time * containerWidth) / this.interval);
    },
    setStart() {
      this.$store.commit('setMarkInTimelineTime', this.markerTime * 1000);
      this.adjustMarkerTime();
      this.adjustSequence();
    },
    setEnd() {
      this.$store.commit('setMarkOutTimelineTime', this.markerTime * 1000);
      this.adjustMarkerTime();
      this.adjustSequence();
    },
    onPreviewClip() {
      this.$store.commit('requestStartPlaying', this.markInTimelineTime);
      this.state.previewClip = true;
    },
    onPressSpacePlay() {
      if (this.state.latestPlay === 'play') {
        this.$store.commit('requestTogglePlaying');
        this.state.latestPlay = 'pause';
      } else if (this.state.latestPlay === 'pause') {
        this.$store.commit('requestStartPlaying');
        this.state.latestPlay = 'play';
      } else {
        this.$store.commit('requestStopPlaying');
        this.state.latestPlay = 'pause';
      }
    },
    onSeekAdd() {
      this.$store.commit('seekPlayheadForward', 1000);
    },
    onSeekAddFrame() {
      this.$store.commit('seekPlayheadForward', 50);
    },
    onSeekSubtract() {
      this.$store.commit('seekPlayheadBackward', 1000);
    },
    onSeekSubtractFrame() {
      this.$store.commit('seekPlayheadBackward', 50);
    },
    onScrollToStart() {
      this.$store.commit('setPlayheadByTimelineTime', this.markInTimelineTime);
    },
    onScrollToEnd() {
      this.$store.commit('setPlayheadByTimelineTime', this.markOutTimelineTime);
    },
    onShowExportModal() {
      this.state.showExportClipModal = !this.state.showExportClipModal;
    },
    onSubmitExportModal() {
      this.state.showExportClipModal = false;
      this.onSubmit(this.chunkPayload);
    },
    scrollToMarker() {
      scrollIntoView(this.$refs.marker, {
        behavior: 'auto',
        inline: 'center',
        scrollMode: 'if-needed',
        boundary: this.$refs.container,
      });
    },
    timeElapsedFromStart(start, end) {
      return differenceInSeconds(start, end);
    },
    getDeadSequenceStart(f) {
      const pos = this.getPositionByTime(f.startTimelineTime / 1000);
      return pos;
    },
    getDeadSequenceWidth(f) {
      const startPos = this.getPositionByTime(f.startTimelineTime / 1000);
      const endPos = this.getPositionByTime(f.endTimelineTime / 1000);
      return endPos - startPos;
    },
    scheduleAutoScroll() {
      this.state.shouldAutoScrollScheduleId = setTimeout(
        () => {
          this.state.shouldAutoScroll = true;
          this.state.shouldAutoScrollScheduleId = null;
          this.scrollToMarker();
        },
        10000,
      );
    },
    setInterval(interval) {
      this.interval = interval;
      this.moveSliderButton();
    },
    zoomIn() {
      switch (this.interval) {
        case 3600:
          this.interval = 1800;
          break;
        case 1800:
          this.interval = 900;
          break;
        case 900:
          this.interval = 300;
          break;
        default:
          this.interval = 300;
      }
      this.moveSliderButton();
      this.scrollToMarker();
    },
    zoomOut() {
      switch (this.interval) {
        case 1800:
          this.interval = 3600;
          break;
        case 900:
          this.interval = 1800;
          break;
        case 300:
          this.interval = 900;
          break;
        default:
          this.interval = 3600;
      }
      this.moveSliderButton();
    },
    getZoomSize() {
      switch (this.interval) {
        case 3600:
          return `1 ${this.$t('glossary:hour', { count: 3600 })}`;
        case 1800:
          return `30 ${this.$t('glossary:minute', { count: 1800 })}`;
        case 900:
          return `15 ${this.$t('glossary:minute', { count: 900 })}`;
        default:
          return `5 ${this.$t('glossary:minute', { count: 5 })}`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';
@import '~@/assets/scss/themes/transition.scss';

.clip-editor-container {
  font-size: 14px;
  font-variant-numeric: tabular-nums lining-nums;

  .editor-help-overlay {
    position: absolute;
    top: 41px;
    left: 0;
    right: 0;
    margin: auto;
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 0;
    opacity: 0.7;
    .help-text {
      cursor: default;
      font-size: $font-size-base * 0.7;
      margin: 0;
      .yellow {
        color: $yellow;
      }
    }
  }

  .editor-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    align-items: flex-end;
    .editor-header-title {
      .timeline-header {
        display: flex;
        flex-flow: row;
        margin-bottom: 6px;
        .timeline-text {
          font-weight: 700;
          margin-bottom: 0;
          margin-right: $spacer;
        }
      }
      .time-text {
        margin-bottom: 0;
      }
    }
    .timeline-timestamp-container {
      position: absolute;
      width: fit-content;
      left: 0;
      right: 0;
      margin: auto;
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      .timeline-timestamp {
        font-weight: 700;
        color: $primary;
        margin-bottom: 0;
      }
    }
    .timeline-zoom-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-flow: column;
      .zoom-text-hint {
        .zoom-size {
          margin: 0 0 -6px;
          font-size: 10px;
        }
      }
      .zoom-slider {
        width: 300px;
        display: flex;
        flex-flow: row;
        align-items: center;
        justify-content: center;
        .slider-icon {
          min-width: 30px;
          min-height: 30px;
          max-width: 30px;
          max-height: 30px;
          width: 30px;
          height: 30px;
        }
        .zoom-in-icon, .zoom-out-icon {
          cursor: pointer;
          width: 30px;
          height: 30px;
          padding: 9px 4px 8px 8px;
          border-radius: 50%;
          transition: all 250ms ease;
          &:hover {
            color: #222;
            background: $body-color;
          }
        }
        .slider-track {
          position: relative;
          display: flex;
          justify-content: space-between;
          flex: 1;
          margin: 0 20px;
          height: 2px;
          background: $body-color;
          .current-point {
            position: absolute;
            background: $body-color;
            width: 20px;
            height: 20px;
            margin-top: -10px;
            border-radius: 50%;
            top: 2px;
            left: 128px;
          }
          .slider-point {
            cursor: pointer;
            margin-top: -3px;
            width: 4px;
            height: 10px;
            background: #969a9f;
            &:last-child {
              margin-right: -2px;
            }
          }
        }
      }
    }
  }

  .clip-editor-wrapper {
    position: relative;

    .clip-editor {
      position: relative;
      overflow-x: scroll;
      overflow-y: hidden;

      .clip-editor__marker {
        position: absolute;
        background-color: $primary;
        height: 85px;
        top: 0;
        width: 2px;
        transition: all .1s ease-in-out;
        z-index: 2;
        cursor: e-resize;
      }

      .clip-editor__sequence {
        position: absolute;
        cursor: pointer;
        height: 54px;
        background-color: #9BA4B4;
        opacity: 0.5;
        top: 31px;
        z-index: 1;
      }

      .clip-editor__dead-sequence {
        position: absolute;
        cursor: grab;
        height: 15vh;
        background: repeating-linear-gradient(
          45deg,
          rgba(0, 0, 0, .3),
          rgba(0, 0, 0, .3) 10px,
          rgba(0, 0, 0, .1) 10px,
          rgba(0, 0, 0, .1) 20px
        );
        top: 0;
        z-index: 1;
      }

      .clip-editor__trimmer-container {
        background: $dark-primary;
        height: 56px;
        z-index: 3;
        cursor: pointer;
      }

      .clip-editor__panner-container {
        display: flex;
        justify-content: space-between;
        white-space: nowrap;
        background-color: rgba(0, 0, 0, .1);
        overflow: hidden;
        cursor: default;

        .time-chunk-container {
          position: relative;
          height: 30px;

          .time-chunk {
            width: 1px;
            height: 60%;
            background-color: black;
          }

          .time-chunk.--small {
            width: 1px;
            height: 6px;
            background-color: #101010;
          }

          .time-chunk__date {
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
          }
        }
      }
    }

    .clip-editor::-webkit-scrollbar {
      cursor: move;
      height: 1em;
    }

    .clip-editor::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    }

    .clip-editor::-webkit-scrollbar-thumb {
      cursor: move;
      border-radius: 20px;
      border: solid 2px $gray-700;
    }
  }
}

.timeline-toolbar {
  position: relative;
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 2px 0;
  background-color: $dark-primary;
  margin-bottom: 40px;
  .btn {
    border-radius: 0;
    .help-text {
      display: none;
      position: absolute;
      bottom: -26px;
      background: #16a6ed;
      color: #222;
      padding: 6px;
      margin: auto;
      left: 0;
      right: 0;
      width: 300px;
      transition: none;
      .key {
        background: #e0e0e0;
        padding: 2px 10px;
        border-radius: 0.25rem;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.4);
      }
    }
    &.current-step {
      animation: background-blinking 1500ms infinite;
    }
    &:hover {
      background: $primary;
      color: #222;
      .help-text {
        display: block;
      }
    }
  }
}

.timeline-actions-container {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  .output-info {
    flex: 1;
    .output-text {
      margin: 0;
    }
  }
  .screenshot-button, .preview-clip-button {
    border: 1px solid $dark-400;
    margin-right: 20px;
    color: #FFF;
    transition: all 250ms ease;
    &:hover {
      background: $dark-400;
    }
  }
}

.error-message {
  margin-top: $spacer;
}

@media screen and (max-width: 1366px) {
  .timeline-toolbar {
    margin-bottom: 20px;
  }
  .time-text {
    font-size: 1em;
  }
}

@media screen and (max-width: 991px){
  .timeline-actions-container {
    flex-flow: column;
    .output-info {
      margin-bottom: $spacer;
    }
    .btn {
      margin: 0 0 10px 0 !important;
    }
  }
}

@media screen and (max-width: 768px) {
  .clip-editor-container {
    .editor-header {
      flex-flow: column;
      justify-content: space-between;
      margin-bottom: 10px;
      align-items: flex-start;
      .timeline-timestamp-container {
        top: 12px;
      }
      .timeline-zoom-container {
        margin-left: auto;
      }
    }
  }
}

@media screen and (max-width: 540px) {
  .clip-editor-container {
    .editor-header {
      .timeline-timestamp-container {
        left: unset;
        right: 10px;
        margin-left: auto;
      }
    }
    .clip-editor-wrapper {
      .clip-editor {
        .clip-editor__panner-container {
          .time-chunk-container:nth-child(2n) {
            display: none;
          }
        }
      }
    }
  }
}

</style>
